'use client';

import React, { useState } from 'react';
import { useTranslations } from 'next-intl';
import Header from '@/components/Header';
import HeroImage from '@/components/HeroImage';
import Footer from '@/components/Footer';
import { motion } from 'framer-motion';
import styles from './page.module.css';
import { contactFormSchema } from '@/lib/validation';
import { sanitizeFormData } from '@/lib/sanitize';
import { logger } from '@/lib/logger';
import { z } from 'zod';
import { LocalBusinessSchema, BreadcrumbSchema } from '@/lib/schema';
import { trackContactFormSubmit } from '@/lib/analytics';


const fadeIn = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

export default function ContactPage() {
  const t = useTranslations('contact');
  const tCommon = useTranslations('common');

  const [formStatus, setFormStatus] = useState<{
    show: boolean;
    type: 'success' | 'error' | 'loading';
    message: string;
  }>({ show: false, type: 'loading', message: '' });
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    // Clear previous validation errors
    setValidationErrors({});

    const formData = new FormData(form);
    const rawData = Object.fromEntries(formData.entries());

    try {
      // Step 1: Validate with Zod schema
      const validatedData = contactFormSchema.parse(rawData);

      // Step 2: Sanitize validated data (remove any HTML)
      const sanitizedData = sanitizeFormData(validatedData);

      // Show loading state
      setFormStatus({
        show: true,
        type: 'loading',
        message: t('status.sending')
      });

      // Submit to Cloud Function API
      const apiBase = process.env.NEXT_PUBLIC_API_URL || '';
      const apiUrl = apiBase ? `${apiBase}/api/contact` : 'https://api-56tpojcbbq-uc.a.run.app/api/contact';
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sanitizedData),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData?.error?.message || `Server error: ${response.status}`);
      }

      // Show success message
      setFormStatus({
        show: true,
        type: 'success',
        message: t('status.success')
      });

      // Track contact form submission
      try {
        trackContactFormSubmit();
      } catch {
        // Analytics errors must never break the form flow
      }

      // Reset form
      form.reset();

      // Hide success message after 5 seconds
      setTimeout(() => {
        setFormStatus({ show: false, type: 'loading', message: '' });
      }, 5000);

    } catch (error) {
      if (error instanceof z.ZodError) {
        // Handle validation errors
        const errors: Record<string, string> = {};
        error.issues.forEach(err => {
          if (err.path[0]) {
            errors[err.path[0] as string] = err.message;
          }
        });
        setValidationErrors(errors);

        setFormStatus({
          show: true,
          type: 'error',
          message: t('status.validationError')
        });

        logger.warn('Validation errors:', errors);
      } else {
        // Handle other errors
        setFormStatus({
          show: true,
          type: 'error',
          message: t('status.error')
        });
        logger.error('Form submission error:', error);
      }
    }
  };

  return (
    <>
      <LocalBusinessSchema />
      <BreadcrumbSchema items={[
        { name: tCommon('home'), href: '/' },
        { name: tCommon('contact'), href: '/contact/' },
      ]} />
      <Header />
      <HeroImage
        backgroundImage="https://images.unsplash.com/photo-1519904981063-b0cf448d479e?w=1920&q=80"
        title={t('hero.title')}
        subtitle={t('hero.subtitle')}
        location={t('hero.location')}
      />

      <main>
        {/* Contact Form & Info Section */}
        <section className={styles.contactSection}>
          <div className={styles.container}>
            <div className={styles.contactGrid}>
              {/* Contact Form */}
              <motion.div className={styles.formContainer} {...fadeIn}>
                <div className={styles.formHeader}>
                  <h1>{t('form.title')}</h1>
                  <p>{t('form.subtitle')}</p>
                </div>

                <form className={styles.contactForm} onSubmit={handleSubmit}>
                  {/* Name */}
                  <div className={styles.formGroup}>
                    <label htmlFor="name">{t('form.fields.name.label')}</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      placeholder={t('form.fields.name.placeholder')}
                      aria-invalid={!!validationErrors.name}
                      aria-describedby={validationErrors.name ? 'name-error' : undefined}
                    />
                    {validationErrors.name && (
                      <span id="name-error" className={styles.fieldError} role="alert">
                        {validationErrors.name}
                      </span>
                    )}
                  </div>

                  {/* Email */}
                  <div className={styles.formGroup}>
                    <label htmlFor="email">{t('form.fields.email.label')}</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      placeholder={t('form.fields.email.placeholder')}
                      aria-invalid={!!validationErrors.email}
                      aria-describedby={validationErrors.email ? 'email-error' : undefined}
                    />
                    {validationErrors.email && (
                      <span id="email-error" className={styles.fieldError} role="alert">
                        {validationErrors.email}
                      </span>
                    )}
                  </div>

                  {/* Phone */}
                  <div className={styles.formGroup}>
                    <label htmlFor="phone">{t('form.fields.phone.label')}</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      placeholder={t('form.fields.phone.placeholder')}
                      aria-invalid={!!validationErrors.phone}
                      aria-describedby={validationErrors.phone ? 'phone-error' : undefined}
                    />
                    {validationErrors.phone && (
                      <span id="phone-error" className={styles.fieldError} role="alert">
                        {validationErrors.phone}
                      </span>
                    )}
                  </div>

                  {/* Country */}
                  <div className={styles.formGroup}>
                    <label htmlFor="country">{t('form.fields.country.label')}</label>
                    <input
                      type="text"
                      id="country"
                      name="country"
                      placeholder={t('form.fields.country.placeholder')}
                      aria-invalid={!!validationErrors.country}
                      aria-describedby={validationErrors.country ? 'country-error' : undefined}
                    />
                    {validationErrors.country && (
                      <span id="country-error" className={styles.fieldError} role="alert">
                        {validationErrors.country}
                      </span>
                    )}
                  </div>

                  {/* Experience Type */}
                  <div className={styles.formGroup}>
                    <label htmlFor="experience">{t('form.fields.experience.label')}</label>
                    <select
                      id="experience"
                      name="experience"
                      required
                      aria-invalid={!!validationErrors.experience}
                      aria-describedby={validationErrors.experience ? 'experience-error' : undefined}
                    >
                      <option value="">{t('form.fields.experience.options.select')}</option>
                      <option value="heli-skiing">{t('form.fields.experience.options.heliSkiing')}</option>
                      <option value="ski-trips">{t('form.fields.experience.options.skiTrips')}</option>
                      <option value="randonnee">{t('form.fields.experience.options.skiTouring')}</option>
                      <option value="fly-fishing">{t('form.fields.experience.options.flyFishing')}</option>
                      <option value="lodge-stay">{t('form.fields.experience.options.lodgeStay')}</option>
                      <option value="multi-sport">{t('form.fields.experience.options.multiSport')}</option>
                      <option value="custom">{t('form.fields.experience.options.custom')}</option>
                    </select>
                    {validationErrors.experience && (
                      <span id="experience-error" className={styles.fieldError} role="alert">
                        {validationErrors.experience}
                      </span>
                    )}
                  </div>

                  {/* Dates */}
                  <div className={styles.formGroupRow}>
                    <div className={styles.formGroup}>
                      <label htmlFor="startDate">{t('form.fields.startDate.label')}</label>
                      <input
                        type="date"
                        id="startDate"
                        name="startDate"
                        aria-invalid={!!validationErrors.startDate}
                        aria-describedby={validationErrors.startDate ? 'startDate-error' : undefined}
                      />
                      {validationErrors.startDate && (
                        <span id="startDate-error" className={styles.fieldError} role="alert">
                          {validationErrors.startDate}
                        </span>
                      )}
                    </div>
                    <div className={styles.formGroup}>
                      <label htmlFor="duration">{t('form.fields.duration.label')}</label>
                      <input
                        type="number"
                        id="duration"
                        name="duration"
                        min="1"
                        max="30"
                        placeholder={t('form.fields.duration.placeholder')}
                        aria-invalid={!!validationErrors.duration}
                        aria-describedby={validationErrors.duration ? 'duration-error' : undefined}
                      />
                      {validationErrors.duration && (
                        <span id="duration-error" className={styles.fieldError} role="alert">
                          {validationErrors.duration}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Group Size */}
                  <div className={styles.formGroup}>
                    <label htmlFor="groupSize">{t('form.fields.groupSize.label')}</label>
                    <input
                      type="number"
                      id="groupSize"
                      name="groupSize"
                      min="1"
                      max="20"
                      placeholder={t('form.fields.groupSize.placeholder')}
                      aria-invalid={!!validationErrors.groupSize}
                      aria-describedby={validationErrors.groupSize ? 'groupSize-error' : undefined}
                    />
                    {validationErrors.groupSize && (
                      <span id="groupSize-error" className={styles.fieldError} role="alert">
                        {validationErrors.groupSize}
                      </span>
                    )}
                  </div>

                  {/* Experience Level */}
                  <div className={styles.formGroup}>
                    <label htmlFor="level">{t('form.fields.level.label')}</label>
                    <select
                      id="level"
                      name="level"
                      aria-invalid={!!validationErrors.level}
                      aria-describedby={validationErrors.level ? 'level-error' : undefined}
                    >
                      <option value="">{t('form.fields.level.options.select')}</option>
                      <option value="beginner">{t('form.fields.level.options.beginner')}</option>
                      <option value="intermediate">{t('form.fields.level.options.intermediate')}</option>
                      <option value="advanced">{t('form.fields.level.options.advanced')}</option>
                      <option value="expert">{t('form.fields.level.options.expert')}</option>
                    </select>
                    {validationErrors.level && (
                      <span id="level-error" className={styles.fieldError} role="alert">
                        {validationErrors.level}
                      </span>
                    )}
                  </div>

                  {/* Message */}
                  <div className={styles.formGroup}>
                    <label htmlFor="message">{t('form.fields.message.label')}</label>
                    <textarea
                      id="message"
                      name="message"
                      rows={5}
                      required
                      placeholder={t('form.fields.message.placeholder')}
                      aria-invalid={!!validationErrors.message}
                      aria-describedby={validationErrors.message ? 'message-error' : undefined}
                    ></textarea>
                    {validationErrors.message && (
                      <span id="message-error" className={styles.fieldError} role="alert">
                        {validationErrors.message}
                      </span>
                    )}
                  </div>

                  {/* Budget */}
                  <div className={styles.formGroup}>
                    <label htmlFor="budget">{t('form.fields.budget.label')}</label>
                    <select
                      id="budget"
                      name="budget"
                      aria-invalid={!!validationErrors.budget}
                      aria-describedby={validationErrors.budget ? 'budget-error' : undefined}
                    >
                      <option value="">{t('form.fields.budget.options.notSpecify')}</option>
                      <option value="5000-10000">{t('form.fields.budget.options.range1')}</option>
                      <option value="10000-20000">{t('form.fields.budget.options.range2')}</option>
                      <option value="20000-50000">{t('form.fields.budget.options.range3')}</option>
                      <option value="50000+">{t('form.fields.budget.options.range4')}</option>
                    </select>
                    {validationErrors.budget && (
                      <span id="budget-error" className={styles.fieldError} role="alert">
                        {validationErrors.budget}
                      </span>
                    )}
                  </div>

                  {/* Submit Button */}
                  <button type="submit" className={styles.submitButton}>
                    {t('form.submitButton')}
                  </button>

                  {/* Form Status Messages */}
                  {formStatus.show && (
                    <div className={`${styles.formStatus} ${styles[formStatus.type]}`}>
                      {formStatus.message}
                    </div>
                  )}
                </form>
              </motion.div>

              {/* Contact Info Sidebar */}
              <motion.aside className={styles.contactInfo} {...fadeIn} transition={{ delay: 0.2 }}>
                <div className={styles.infoCard}>
                  <h3>{t('sidebar.contactInfo.title')}</h3>

                  <div className={styles.infoItem}>
                    <div className={styles.infoIcon}>📧</div>
                    <div className={styles.infoContent}>
                      <p className={styles.infoLabel}>{t('sidebar.contactInfo.email')}</p>
                      <a href="mailto:info@apexexperience.cl">info@apexexperience.cl</a>
                    </div>
                  </div>

                  <div className={styles.infoItem}>
                    <div className={styles.infoIcon}>📞</div>
                    <div className={styles.infoContent}>
                      <p className={styles.infoLabel}>{t('sidebar.contactInfo.phone')}</p>
                      <a href="tel:+56991998708">+56 9 9199 8708</a>
                    </div>
                  </div>

                  <div className={styles.infoItem}>
                    <div className={styles.infoIcon}>📍</div>
                    <div className={styles.infoContent}>
                      <p className={styles.infoLabel}>{t('sidebar.contactInfo.office')}</p>
                      <p>{t('sidebar.contactInfo.officeAddress')}</p>
                    </div>
                  </div>

                  <div className={styles.infoItem}>
                    <div className={styles.infoIcon}>⏰</div>
                    <div className={styles.infoContent}>
                      <p className={styles.infoLabel}>{t('sidebar.contactInfo.hours')}</p>
                      <p>{t('sidebar.contactInfo.hoursWeekdays')}</p>
                      <p>{t('sidebar.contactInfo.hoursSaturday')}</p>
                    </div>
                  </div>
                </div>

                <div className={styles.infoCard}>
                  <h3>{t('sidebar.directCall.title')}</h3>
                  <p className={styles.infoDescription}>
                    {t('sidebar.directCall.description')}
                  </p>
                  <a href="https://calendly.com/apexexperience" target="_blank" rel="noopener noreferrer" className={styles.scheduleButton}>
                    {t('sidebar.directCall.button')}
                  </a>
                </div>

                <div className={styles.infoCard}>
                  <h3>{t('sidebar.responseGuarantee.title')}</h3>
                  <p className={styles.infoDescription}>
                    {t('sidebar.responseGuarantee.description')}
                  </p>
                </div>
              </motion.aside>
            </div>
          </div>
        </section>

      </main>

      <Footer />
    </>
  );
}
